import useAddRequestStore from 'store/addRequestStore';
import { NO_PLOT_AREA_PROPERTY_TYPES, PROPERTY_TYPES } from 'util/Constants';
import AddRequestHeader from './AddRequestHeader';
import Dropdown from './DropDown';
import RangeFilter from './RangeFilter';

interface PropertyDetailsProps {
    isEditMode?: boolean;
}

export default function PropertyDetails({ isEditMode }: PropertyDetailsProps) {
    const { requestData, setRequestData, clearErrors, errors, setErrors } = useAddRequestStore();

    // Helper function to check if all selected property types are in NO_PLOT_AREA_PROPERTY_TYPES
    const areAllSelectedTypesNoPlotArea = () => {
        if (!requestData.propertyType) return false;
        const selectedTypes = requestData.propertyType.split(', ');
        return selectedTypes.every((type) => NO_PLOT_AREA_PROPERTY_TYPES.includes(type));
    };

    // Helper function to check if only Land and/or Plot are selected
    const areOnlyLandOrPlotSelected = () => {
        if (!requestData.propertyType) return false;
        const selectedTypes = requestData.propertyType.split(', ');
        return selectedTypes.length > 0 && selectedTypes.every((type) => type === 'Land' || type === 'Plot');
    };

    return (
        <>
            <AddRequestHeader title="Property Specifications" description="Define the type and size of the property you need." />
            <div className="lg:space-y-10 space-y-5 mt-7 lg:mt-8">
                <ItemContainer
                    title="Property Type"
                    description="Select the type of property you're interested"
                    children={
                        <>
                            <div className={`${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-10/12 default-md:w-2/3 default-sm:w-full'}`}>
                                <Dropdown
                                    title="Property Type"
                                    description="Select a property type to filter listings that match your needs"
                                    options={PROPERTY_TYPES}
                                    isShown={false}
                                    heightClass="h-12"
                                    isEditMode={isEditMode}
                                    value={requestData.propertyType}
                                    onChange={(value) => {
                                        setRequestData({ propertyType: value as string });
                                        clearErrors('propertyType');
                                        clearErrors('minConstructionArea');
                                        clearErrors('maxConstructionArea');
                                        clearErrors('minPlotArea');
                                        clearErrors('maxPlotArea');
                                    }}
                                    showClearButton={false}
                                    multiple={true}
                                />
                            </div>
                            {errors.propertyType && <p className="text-red-500 text-sm !mt-2">{errors.propertyType}</p>}
                        </>
                    }
                />

                {!areOnlyLandOrPlotSelected() && (
                    <ItemContainer
                        title="Constructed Area"
                        description="Enter the minimum & maximum size you're looking for, or adjust the slider to set a range"
                        children={
                            <>
                                <div className="flex flex-col space-y-4">
                                    <RangeFilter
                                        showBorderAndText={false}
                                        unit="m²"
                                        classNames={`${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-5/12 default-md:w-2/3 default-sm:w-full'}`}
                                        value={[requestData.minConstructionArea, requestData.maxConstructionArea]}
                                        onChanged={(value) => {
                                            if (value[0] > value[1]) {
                                                setErrors({
                                                    ...errors,
                                                    maxConstructionArea: 'Maximum construction area cannot be less than minimum construction area',
                                                });
                                            } else {
                                                clearErrors('minConstructionArea');
                                                clearErrors('maxConstructionArea');
                                            }

                                            setRequestData({ minConstructionArea: value[0], maxConstructionArea: value[1] });
                                        }}
                                        limit={[0, 8000]}
                                        steps={[
                                            { max: 500, step: 50 },
                                            { max: 2000, step: 100 },
                                            { max: 8000, step: 500 },
                                        ]}
                                    />
                                </div>
                                {(errors.minConstructionArea || errors.maxConstructionArea) && (
                                    <p className="text-red-500 text-sm !mt-2">{errors.minConstructionArea || errors.maxConstructionArea}</p>
                                )}
                            </>
                        }
                    />
                )}

                {(!areAllSelectedTypesNoPlotArea() || !requestData.propertyType) && (
                    <ItemContainer
                        title="Plot Area"
                        description="Enter the minimum & maximum size you're looking for, or adjust the slider to set a range"
                        children={
                            <>
                                <div className="flex flex-col space-y-4">
                                    <RangeFilter
                                        showBorderAndText={false}
                                        unit="m²"
                                        classNames={`${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-5/12 default-md:w-2/3 default-sm:w-full'}`}
                                        value={[requestData.minPlotArea, requestData.maxPlotArea]}
                                        onChanged={(value) => {
                                            if (value[0] > value[1]) {
                                                setErrors({
                                                    ...errors,
                                                    maxPlotArea: 'Maximum plot area cannot be less than minimum plot area',
                                                });
                                            } else {
                                                clearErrors('minPlotArea');
                                                clearErrors('maxPlotArea');
                                            }

                                            setRequestData({ minPlotArea: value[0], maxPlotArea: value[1] });
                                        }}
                                        limit={[0, 5000]}
                                        steps={[
                                            { max: 5000, step: 100 },
                                            { max: 15000, step: 1000 },
                                            { max: 100000, step: 5000 },
                                        ]}
                                    />
                                </div>
                                {(errors.minPlotArea || errors.maxPlotArea) && (
                                    <p className="text-red-500 text-sm !mt-2">{errors.minPlotArea || errors.maxPlotArea}</p>
                                )}
                            </>
                        }
                    />
                )}

                {!areOnlyLandOrPlotSelected() && (
                    <ItemContainer
                        title="Bedrooms"
                        description="Set your preferred minimum and maximum number of bedrooms"
                        children={
                            <>
                                <div
                                    className={`flex gap-4 flex-1 ${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-5/12 default-md:w-2/3 default-sm:w-full'}`}
                                >
                                    <Dropdown
                                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                        isShown={false}
                                        heightClass="h-12"
                                        value={requestData.minBedrooms}
                                        onChange={(value) => {
                                            if ((value as number) > requestData.maxBedrooms) {
                                                setErrors({ ...errors, minBedrooms: 'Minimum bedrooms cannot be greater than maximum bedrooms' });
                                            } else {
                                                clearErrors('minBedrooms');
                                                clearErrors('maxBedrooms');
                                            }
                                            setRequestData({ minBedrooms: value as number });
                                        }}
                                        showClearButton={false}
                                    />
                                    <h2 className="self-center font-medium text-lg text-gray-400">To</h2>
                                    <Dropdown
                                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                        isShown={false}
                                        heightClass="h-12"
                                        value={requestData.maxBedrooms}
                                        onChange={(value) => {
                                            if ((value as number) < requestData.minBedrooms) {
                                                setErrors({ ...errors, maxBedrooms: 'Maximum bedrooms cannot be less than minimum bedrooms' });
                                            } else {
                                                clearErrors('maxBedrooms');
                                                clearErrors('minBedrooms');
                                            }
                                            setRequestData({ maxBedrooms: value as number });
                                        }}
                                        showClearButton={false}
                                    />
                                </div>
                                {(errors.minBedrooms || errors.maxBedrooms) && (
                                    <p className="text-red-500 text-sm !mt-2">{errors.minBedrooms || errors.maxBedrooms}</p>
                                )}
                            </>
                        }
                        showBorder={true}
                    />
                )}

                {!areOnlyLandOrPlotSelected() && (
                    <ItemContainer
                        title="Bathrooms"
                        description="Set your preferred minimum and maximum number of bathrooms"
                        children={
                            <>
                                <div
                                    className={`flex gap-4 flex-1 ${isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-5/12 default-md:w-2/3 default-sm:w-full'}`}
                                >
                                    <Dropdown
                                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                        isShown={false}
                                        heightClass="h-12"
                                        value={requestData.minBathrooms}
                                        onChange={(value) => {
                                            if ((value as number) > requestData.maxBathrooms) {
                                                setErrors({ ...errors, minBathrooms: 'Minimum bathrooms cannot be greater than maximum bathrooms' });
                                            } else {
                                                clearErrors('minBathrooms');
                                                clearErrors('maxBathrooms');
                                            }
                                            setRequestData({ minBathrooms: value as number });
                                        }}
                                        showClearButton={false}
                                    />
                                    <h2 className="self-center font-medium text-lg text-gray-400">To</h2>
                                    <Dropdown
                                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                        isShown={false}
                                        heightClass="h-12"
                                        value={requestData.maxBathrooms}
                                        onChange={(value) => {
                                            if ((value as number) < requestData.minBathrooms) {
                                                setErrors({ ...errors, maxBathrooms: 'Maximum bedrooms cannot be less than minimum bedrooms' });
                                            } else {
                                                clearErrors('maxBathrooms');
                                                clearErrors('minBathrooms');
                                            }
                                            setRequestData({ maxBathrooms: value as number });
                                        }}
                                        showClearButton={false}
                                    />
                                </div>
                                {(errors.minBathrooms || errors.maxBathrooms) && (
                                    <p className="text-red-500 text-sm !mt-2">{errors.minBathrooms || errors.maxBathrooms}</p>
                                )}
                            </>
                        }
                        showBorder={false}
                    />
                )}
            </div>
        </>
    );
}

interface Props {
    title: string;
    description: string;
    children?: React.ReactNode;
    showBorder?: boolean;
}

function ItemContainer({ title, description, children, showBorder = true }: Props) {
    return (
        <>
            <div className="space-y-2 lg:space-y-3">
                <label htmlFor="title" className="block text-base  font-semibold text-light-black">
                    {title}
                </label>
                <p className="text-[12px]  text-light-black/80 mb-2">{description}</p>
                {children}
            </div>
            {showBorder && <div className="border border-gray-100  w-full " />}
        </>
    );
}
